import {
	NewJoinersAndAllocationsContractDataItem,
	NewJoinersAndAllocationsInvestorLocal,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/useNewJoinersAndAllocationsModalController';
import {
	CHIP_THEME,
	COLOR,
	Chips as ChipsV2,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { PlusCircleIconButton } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Checkbox } from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import {
	DefaultDropdownMenu,
	Dropdown,
} from '@dealstack/frontend/shared/components/fundament/Dropdown/Dropdown';
import { DropdownWrapper } from '@dealstack/frontend/shared/components/fundament/Dropdown/DropdownWrapper';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useState } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	sideAgreements?: NewJoinersAndAllocationsContractDataItem[];
	onUpdateInvestorContracts: (contracts: string[], investorId: string) => void;
	investor: NewJoinersAndAllocationsInvestorLocal;
	index: number;
}

const NewJoinersAndAllocationsInvestorSideAgreementsPopup = ({
	sideAgreements,
	onUpdateInvestorContracts,
	investor,
	className,
}: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const [isOpen, setIsOpen] = useState(false);

	const totalChecked = investor?.contracts?.reduce((total, contractId) => {
		if (sideAgreements?.find((agreement) => agreement.id === contractId)) {
			return ++total;
		}
		return total;
	}, 0);

	return (
		<div className={className}>
			{totalChecked ? (
				<EditWrapper onClick={() => setIsOpen(true)}>
					<ChipsV2
						prefixIcon="checkmark-circle-contained-filled"
						size="s"
						color={COLOR.GREEN}
						chipTheme={CHIP_THEME.LIGHT}
					>
						{totalChecked}
					</ChipsV2>
					<Icon variant="edit-pencil" size="lg" />
				</EditWrapper>
			) : (
				<PlusCircleIconButton onClick={() => setIsOpen(true)} />
			)}

			<DropdownWrapper>
				{isOpen ? (
					<Dropdown
						closeOnScroll
						onClose={() => {
							setIsOpen(false);
						}}
					>
						<DefaultDropdownMenu>
							<SectionHeader
								size="p-lgb"
								title={tMipCreate('investors.table.header.add_side_agreements')}
							/>
							<Divider />
							<SideAgreementsList>
								{sideAgreements?.map((agreement) => {
									const isChecked = !!investor.contracts?.find(
										(investorContractId) => investorContractId === agreement.id
									);

									const onClick = () => {
										const updatedContracts: string[] = isChecked
											? investor.contracts?.filter(
													(investorContractId) =>
														investorContractId !== agreement.id
												) ?? []
											: [...(investor.contracts || []), agreement.id];
										onUpdateInvestorContracts(updatedContracts, investor.id);
									};

									return (
										<Checkbox
											onClick={onClick}
											checked={isChecked}
											label={agreement.name}
											key={agreement.name}
										/>
									);
								})}
								<Button
									variant="outlined"
									onClick={() => {
										setIsOpen(false);
									}}
								>
									{tMipCreate('investors.table.header.close')}
								</Button>
							</SideAgreementsList>
						</DefaultDropdownMenu>
					</Dropdown>
				) : null}
			</DropdownWrapper>
		</div>
	);
};

const SideAgreementsList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1em;
	padding: 0.5em;

	${Button} {
		width: 100%;
	}
`;

const EditWrapper = styled.div`
	display: flex;
	gap: 0.3em;
	justify-content: center;
	align-items: center;
	cursor: pointer;

	.svg {
		color: ${({ theme }) => theme.v2.color.secondary[800]};
	}
`;

const Styled = styled(NewJoinersAndAllocationsInvestorSideAgreementsPopup)`
	margin-right: 1em;

	${DropdownWrapper} {
		position: absolute !important;
		z-index: 10;
		right: 2em;
	}

	${DefaultDropdownMenu} {
		width: max-content;
	}

	${SectionHeader} {
		padding: 0.5em;
	}
`;

export { Styled as NewJoinersAndAllocationsInvestorSideAgreementsPopup };
